import { PricingMethod } from '../price.book';
import { AdjustmentType, CostSource } from '../shared/code';
import { QuotePolicy } from '../policies/quote.policy.types';
import { QuotePro } from './quote.pro';

/**
 * Create-time defaults for a Quote, shared by the v2 surface and the API create handler.
 *
 * These values used to live only in the legacy server builder (`QuoteProLogic.buildQuote`), which
 * the v2 surface never calls — so a v2-created quote persisted `costSource`, `pricingMethod` and
 * `multiplier` as null and priced nothing correctly. They live here so the client's seed and the
 * server's safety net cannot disagree about what a new quote starts as.
 */

/** Fallback expiry window when the policy names none (legacy `getDateRange`). */
export const QUOTE_DEFAULT_EXPIRY_DAYS = 30;

/** What "cost" and "sell" mean on a new quote until the user says otherwise. */
export const QUOTE_PRICING_DEFAULTS: Readonly<Pick<QuotePro, 'costSource' | 'pricingMethod' | 'multiplier'>> = {
  costSource: CostSource.StandardCost,
  pricingMethod: PricingMethod.Markup,
  multiplier: 30,
};

/** Legacy seeded zeros, not nulls — a null budget reads as "unset" everywhere downstream. */
export const QUOTE_BUDGET_DEFAULT = { labour: 0, service: 0, material: 0 } as const;

/** Adjustment type a new line takes. Legacy read the policy with a 'Percent' fallback. */
export function quoteDefaultAdjustmentType(quote: Pick<QuotePro, 'policy'> | null | undefined): AdjustmentType {

  return quote?.policy?.defaults?.adjustmentType ?? 'Percent';
}

/** Expiry window a quote should use: the policy's, else the 30-day fallback. */
export function quoteExpiryDays(policy: QuotePolicy | null | undefined): number {

  const days = policy?.defaults?.expiryDays;

  return days > 0 ? days : QUOTE_DEFAULT_EXPIRY_DAYS;
}

/**
 * Fill only the pricing/budget defaults a payload has not already set.
 *
 * Absent, not falsy: a multiplier the user deliberately set to 0 is a real choice and must survive.
 */
export function withQuoteCreateDefaults<T extends Partial<QuotePro>>(quote: T): T {

  return {
    ...quote,
    costSource: quote.costSource ?? QUOTE_PRICING_DEFAULTS.costSource,
    pricingMethod: quote.pricingMethod ?? QUOTE_PRICING_DEFAULTS.pricingMethod,
    multiplier: quote.multiplier ?? QUOTE_PRICING_DEFAULTS.multiplier,
    budget: quote.budget ?? { ...QUOTE_BUDGET_DEFAULT },
  };
}
